/**
 * Bridge to Shopify Standard Storefront Events & Actions.
 * https://shopify.dev/docs/storefronts/themes/best-practices/standard-events
 *
 * - Loads the Shopify-hosted library (window.StandardEvents) and registers <storefront-view-event>.
 * - Dispatches shopify:page:view, then lets the view elements report.
 * - Configures Shopify.actions.updateCart / openCart to go through the theme's cart drawer / cart page.
 * - Exposes FoxTheme.StandardEvents helpers used by the cart, product, facets and search code.
 *
 * Everything degrades to a silent no-op when the CDN is unreachable, so storefront flows never depend on it.
 * Handles returned by dispatch() can be settled at any time, even before the library has loaded.
 *
 * Settling rules for events that carry a promise (every code path must settle, or listeners wait forever):
 * - resolve when the storefront accepted OR declined the change (declined: pass userErrors; no shopify:cart:error)
 * - reject when the request itself failed (also dispatches shopify:cart:error)
 */
(() => {
  const LIB_URL = 'https://cdn.shopify.com/storefront/standard-events.js';

  let libPromise;
  /** @returns {Promise<object | null>} */
  const loadLib = () => {
    if (!libPromise) {
      libPromise = import(LIB_URL)
        .then((lib) => {
          window.StandardEvents = lib;
          return lib;
        })
        .catch((error) => {
          console.warn('[Standard Events] library unavailable:', error);
          return null;
        });
    }
    return libPromise;
  };

  /** Run `callback(lib)` now if the library is loaded, otherwise as soon as it is. */
  const withLib = (callback) => {
    if (window.StandardEvents) callback(window.StandardEvents);
    else loadLib().then((lib) => lib && callback(lib));
  };

  const createHandles = () => {
    const handles = { settled: false };
    handles.promise = new Promise((resolve, reject) => {
      handles.resolve = (value) => {
        handles.settled = true;
        resolve(value);
      };
      handles.reject = (error) => {
        handles.settled = true;
        reject(error);
      };
    });
    // Listeners get their own rejection: our reference must never surface as "unhandled"
    handles.promise.catch(() => {});
    return handles;
  };

  const StandardEvents = {
    loadLib,

    /**
     * Dispatch an event that carries a promise. Returns handles to settle it.
     * @param {EventTarget | null} target
     * @param {string} className e.g. "CartLinesUpdateEvent"
     * @param {object | Function} payload An object, or a function of the library for data that needs its helpers
     * @returns {{ promise: Promise<any>, resolve: Function, reject: Function, target: EventTarget | null }}
     */
    dispatch(target, className, payload = {}) {
      const handles = createHandles();
      handles.target = target;
      withLib((lib) => {
        const EventClass = lib[className];
        if (!EventClass) return;
        try {
          const data = typeof payload === 'function' ? payload(lib) : payload;
          (target || document).dispatchEvent(new EventClass({ ...data, promise: handles.promise }));
        } catch (error) {
          console.warn('[Standard Events] dispatch failed:', className, error);
        }
      });
      return handles;
    },

    /** Dispatch an event without a promise (view events, errors). */
    dispatchPlain(target, className, payload = {}) {
      withLib((lib) => {
        const EventClass = lib[className];
        if (!EventClass) return;
        try {
          (target || document).dispatchEvent(new EventClass(payload));
        } catch (error) {
          console.warn('[Standard Events] dispatch failed:', className, error);
        }
      });
    },

    /** shopify:cart:error: a request that FAILED. A declined change is reported through userErrors instead. */
    cartError(target, error, code = 'SERVICE_UNAVAILABLE') {
      this.dispatchPlain(target, 'CartErrorEvent', {
        error: typeof error === 'string' ? error : error?.message || 'Cart request failed',
        code,
      });
    },

    /**
     * The message of a declined cart request (change.js / update.js answer `{ errors }`, add.js `{ status, description }`),
     * or null when the response is a cart.
     */
    declinedMessage(response) {
      if (response?.errors) return String(response.errors);
      if (response?.status) return response.description || response.message || 'Cart request declined';
      return null;
    },

    /**
     * Resolve a cart-carrying event (lines / note / discount).
     * @param {object | null} handles
     * @param {object} [options]
     * @param {object} [options.ajaxCart] A full /cart.js-shaped cart if you already have it, avoids an extra request
     * @param {Array<{code: string, message: string}>} [options.userErrors] Set when the storefront declined the change
     * @param {Array<{code?: string, message: string}>} [options.warnings]
     * @param {(cart: object) => boolean} [options.isApplied] Tells a partly applied change from a declined one. The
     *   Ajax API answers a quantity capped at the stock available with the same error as a declined change, so when
     *   this returns true for the resulting cart, userErrors are reported as a stock warning instead.
     */
    async resolveCart(handles, { ajaxCart, userErrors = [], warnings = [], isApplied } = {}) {
      if (!handles || handles.settled) return;
      // Settled from here on: an error thrown by the UI code that runs after this is not a failed cart request
      handles.settled = true;
      try {
        const lib = await loadLib();
        if (!lib) return handles.resolve({ cart: null, userErrors, warnings });
        const cart = ajaxCart ?? (await (await fetch(`${FoxTheme.routes.cart_url}.js`)).json());
        if (userErrors.length && isApplied?.(cart)) {
          warnings = userErrors.map(({ message }) => ({ code: 'MERCHANDISE_NOT_ENOUGH_STOCK', message }));
          userErrors = [];
        }
        handles.resolve({
          cart: lib.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          userErrors,
          warnings,
        });
      } catch (error) {
        handles.reject(error);
      }
    },

    /**
     * Reject after a failed request and emit shopify:cart:error. An AbortError is a cancellation, not a failure.
     * Does nothing once the event is settled (e.g. the request succeeded and later UI code threw).
     */
    rejectCart(handles, target, error) {
      if (!handles || handles.settled) return;
      handles.reject(error);
      if (error?.name === 'AbortError') return;
      this.cartError(target, error);
    },

    /**
     * Cart line key (the `id` standard events use for update/remove) of a variant, read from the rendered cart
     * (drawer or cart page). Null when the variant isn't in the cart or no cart is rendered.
     */
    lineKeyForVariant(variantId) {
      return document.querySelector(`[data-cart-item][data-line-key^="${variantId}:"]`)?.dataset.lineKey ?? null;
    },

    /**
     * Quantity of a variant (all its lines) in the cart as rendered right now (drawer or cart page). 0 when a cart is
     * rendered but the variant is not in it, null when no cart is rendered (nothing to compare with).
     */
    renderedCartQuantity(variantId) {
      const input = document.querySelector(`[data-cart-item] [data-quantity-variant-id="${variantId}"]`);
      if (input) return Number(input.dataset.cartQuantity) || 0;
      return document.querySelector('cart-drawer, cart-items') ? 0 : null;
    },

    /**
     * For an add: builds the `isApplied` check telling that the cart really took (part of) it. The Ajax API answers an
     * add capped by stock ("Only 2 items were added to your cart due to availability") with an error, though the cart
     * changed, so compare the quantities now in the cart with those before the request. Call it BEFORE the request.
     * The quantities before come from the cart rendered on the page (no request is made). Undefined when no cart is
     * rendered: the add then counts as declined.
     * @param {Array<string | number>} variantIds
     * @returns {((cart: object) => boolean) | undefined}
     */
    addedToCartChecker(variantIds) {
      // Nothing will be reported without the library
      if (!window.StandardEvents) return undefined;
      try {
        const previous = variantIds.map((id) => [String(id), this.renderedCartQuantity(id)]);
        if (previous.some(([, quantity]) => quantity === null)) return undefined;
        return (cart) =>
          previous.some(
            ([id, quantity]) =>
              cart.items
                .filter((item) => String(item.variant_id) === id)
                .reduce((sum, item) => sum + item.quantity, 0) > quantity
          );
      } catch (error) {
        return undefined;
      }
    },

    /**
     * For flows that post cart/update.js `updates` keyed by variant id ({ [variantId]: newQuantity }).
     * Splits them into add / update / remove events. Returns the handles to settle with resolveCartAll / rejectCartAll.
     * Each handle carries `previousQuantities` ({ [variantId]: quantity before }) for an `isApplied` check, when known.
     * @param {(variantId: string) => { key: string | null, quantity?: number }} [lookup] Where the variant's cart line
     *   comes from. Defaults to the cart rendered on the page; pass one when the caller holds the state itself
     *   (a variant with `quantity` 0 or no `key` is reported as an add; a known `quantity` skips unchanged variants).
     */
    dispatchVariantUpdates(
      target,
      context,
      updates,
      lookup = (variantId) => ({ key: this.lineKeyForVariant(variantId) })
    ) {
      const groups = { add: [], update: [], remove: [] };
      const previous = { add: {}, update: {}, remove: {} };
      for (const [variantId, value] of Object.entries(updates || {})) {
        const quantity = Number(value);
        if (!Number.isFinite(quantity)) continue;
        const { key, quantity: currentQuantity } = lookup(variantId);
        if (currentQuantity === quantity || (currentQuantity > 0 && !key)) continue; // unchanged, or a row without a line key
        const inCart = key && currentQuantity !== 0;
        const action = inCart ? (quantity === 0 ? 'remove' : 'update') : 'add';
        if (action === 'add' && quantity === 0) continue;
        groups[action].push(inCart ? { id: key, quantity } : { merchandiseId: this.variantGid(variantId), quantity });
        if (currentQuantity !== undefined) previous[action][variantId] = currentQuantity;
      }
      return Object.entries(groups)
        .filter(([, lines]) => lines.length > 0)
        .map(([action, lines]) => {
          const handles = this.dispatch(target, 'CartLinesUpdateEvent', { action, context, lines });
          handles.previousQuantities = previous[action];
          return handles;
        });
    },

    /** Resolve every handle with the same outcome. */
    resolveCartAll(handlesList, options) {
      return Promise.all((handlesList || []).map((handles) => this.resolveCart(handles, options)));
    },

    /** Reject every handle, but emit shopify:cart:error only once. */
    rejectCartAll(handlesList, target, error) {
      (handlesList || []).forEach((handles, index) => {
        if (index === 0) this.rejectCart(handles, target, error);
        else if (!handles.settled) handles.reject(error);
      });
    },

    /**
     * shopify:collection:update (collection page) or shopify:search:update (search page) for a filter / sort change.
     * Settle with settleResultsUpdate() once the re-rendered grid is in the DOM.
     * @param {EventTarget} target
     * @param {URLSearchParams} params Query params of the new state (filter.*, sort_by, q)
     */
    dispatchResultsUpdate(target, params) {
      const grid = document.getElementById('ProductGridContainer');
      const isSearch = grid?.dataset.view === 'search';
      const handles = this.dispatch(target, isSearch ? 'SearchUpdateEvent' : 'CollectionUpdateEvent', (lib) => {
        const EventClass = isSearch ? lib.SearchUpdateEvent : lib.CollectionUpdateEvent;
        const productFilters = EventClass.parseProductFilters(params) || [];
        const sortKey = EventClass.getSortKey(params);
        const refinements = {
          ...(productFilters.length ? { productFilters } : {}),
          ...(sortKey ? { sortKey } : {}),
        };

        if (isSearch) return { search: { query: params.get('q') || '', ...refinements } };

        const collectionId = grid?.dataset.collectionId;
        return {
          collection: {
            id: collectionId ? `gid://shopify/Collection/${collectionId}` : null,
            handle: grid?.dataset.collectionHandle || '',
            // The whole collection, before any filter (what the event documents), not the products shown now
            productsCount: Number(grid?.dataset.collectionTotal) || 0,
          },
          ...refinements,
        };
      });
      return { handles, isSearch };
    },

    /**
     * shopify:search:update for a search the buyer just ran: the results page loaded for a submitted query.
     * Refining it (filter / sort) is dispatchResultsUpdate().
     */
    dispatchSearchRun() {
      const grid = document.getElementById('ProductGridContainer');
      const params = new URLSearchParams(window.location.search);
      if (grid?.dataset.view !== 'search' || !params.get('q')) return;

      const handles = this.dispatch(grid, 'SearchUpdateEvent', (lib) => {
        const productFilters = lib.SearchUpdateEvent.parseProductFilters(params) || [];
        const sortKey = lib.SearchUpdateEvent.getSortKey(params);
        return {
          search: {
            query: params.get('q'),
            ...(productFilters.length ? { productFilters } : {}),
            ...(sortKey ? { sortKey } : {}),
          },
        };
      });
      handles.resolve({ totalCount: Number(grid.dataset.resultsCount) || 0 });
    },

    /**
     * Settle a results update. Pass the fetched section HTML on success, or an error if the render failed.
     * @param {{ handles: object, isSearch: boolean } | null} update
     * @param {string} [responseText] The section HTML now rendered
     * @param {Error} [error]
     */
    settleResultsUpdate(update, responseText, error) {
      if (!update) return;
      const { handles, isSearch } = update;
      if (handles.settled) return;
      if (error) return handles.reject(error);
      const grid = new DOMParser()
        .parseFromString(responseText || '', 'text/html')
        .getElementById('ProductGridContainer');
      const count = Number(grid?.dataset.resultsCount) || 0;
      handles.resolve(isSearch ? { totalCount: count } : { productsCount: count });
    },

    /**
     * Map a theme variant JSON (Shopify AJAX shape: numeric id, price in cents) to the standard ProductVariant.
     * @param {object | null} variant
     * @param {Array<{name: string, value: string}>} selectedOptions
     */
    variantToStandard(variant, selectedOptions = []) {
      if (!variant || !variant.id) return null;
      const currencyCode = window.Shopify?.currency?.active || '';
      // Zero-decimal currencies (JPY) have no cents to show, three-decimal ones (KWD) have one more
      let digits = 2;
      try {
        digits = new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode }).resolvedOptions()
          .maximumFractionDigits;
      } catch (error) {}
      return {
        id: this.variantGid(variant.id),
        title: variant.title || '',
        availableForSale: !!variant.available,
        price: {
          amount: (Number(variant.price) / 100).toFixed(digits),
          currencyCode,
        },
        selectedOptions,
      };
    },

    /** GIDs that events expect for merchandiseId / product id. */
    variantGid: (id) => `gid://shopify/ProductVariant/${id}`,
    productGid: (id) => `gid://shopify/Product/${id}`,
  };

  FoxTheme.StandardEvents = StandardEvents;

  /** `source` of the cartUpdate published after an app's Shopify.actions.updateCart() or `cart:refresh`. */
  const ACTION_SOURCE = 'standard-action';
  StandardEvents.ACTION_SOURCE = ACTION_SOURCE;

  /** `source` of the cartUpdate the theme publishes to re-sync the cart after the buyer's own change was declined. */
  const REFRESH_SOURCE = 'standard-refresh';
  StandardEvents.REFRESH_SOURCE = REFRESH_SOURCE;

  /**
   * Re-render every cart section on the page from the server, then publish `cartUpdate` so the existing subscribers
   * (cart items, drawer, quick order list, count bubble) update. A newer call aborts an older one still in flight.
   *
   * Sections render in the context of the URL they are requested from: `/cart.js` ignores `sections`, and the cart
   * drawer renders nothing on the cart template, so the sections come from the current page.
   * Subscribers render synchronously, so elements connected by this render can read `StandardEvents.renderSource`
   * to tell an app's change from the buyer's (see GiftWrapping).
   * @param {{ source?: string }} [options]
   */
  StandardEvents.refreshCart = async ({ source } = {}) => {
    StandardEvents.refreshController?.abort();
    const controller = new AbortController();
    StandardEvents.refreshController = controller;
    const { signal } = controller;

    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    const sectionIds = [...new Set(sections)];

    const fetchJson = async (url) => {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Request failed: ${response.status} ${url}`);
      return response.json();
    };
    const [cart, renderedSections] = await Promise.all([
      fetchJson(`${FoxTheme.routes.cart_url}.js`),
      sectionIds.length ? fetchJson(`${window.location.pathname}?sections=${sectionIds.join(',')}`) : null,
    ]);

    if (renderedSections) cart.sections = renderedSections;

    StandardEvents.renderSource = source;
    try {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart, source });
    } finally {
      StandardEvents.renderSource = null;
    }
    return cart;
  };

  // The theme's legacy refresh hook (also fired by apps such as FoxKit): re-render the cart, open the drawer on `detail.open`
  document.addEventListener('cart:refresh', async (event) => {
    try {
      await StandardEvents.refreshCart({ source: ACTION_SOURCE });
      if (event.detail?.open === true) document.querySelector('cart-drawer')?.show();
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[Standard Events] failed to refresh the cart:', error);
    }
  });

  // Shopify.actions: route app calls through the theme's cart UI, re-rendering in place (no reload).
  // Needs no CDN library, so it works even if the library is blocked. Only the first configure() call wins.
  let actionsConfigured = false;
  const configureActions = () => {
    const actions = window.Shopify?.actions;
    // Shopify.actions is frozen: track state here, not on the object
    if (!actions || actionsConfigured) return;
    actionsConfigured = true;

    actions.updateCart?.configure({
      eventTarget: () => document.querySelector('cart-drawer, main-cart') ?? document,
      async handler(defaultHandler) {
        const result = await defaultHandler();
        try {
          await StandardEvents.refreshCart({ source: ACTION_SOURCE });
        } catch (error) {
          // A newer refresh superseded this one and renders the latest cart
          if (error.name !== 'AbortError') {
            console.error('[Standard Events] failed to refresh the cart after updateCart; reloading.', error);
            window.location.reload();
          }
        }
        return result;
      },
    });

    actions.openCart?.configure({
      async handler() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && FoxTheme.settings.cartType === 'drawer') {
          // A quick view would sit on top of the drawer: close it first
          document.querySelectorAll('quick-view-modal').forEach((modal) => modal.open && modal.hide());
          if (!drawer.open) await drawer.show();
          return;
        }

        // An empty cart renders no <main-cart>, so the template is the reliable sign of the cart page
        if (document.body.classList.contains('cart-template')) {
          // Already on the cart page: nothing to open
          const cart = document.querySelector('main-cart') || document.querySelector('.cart');
          cart?.scrollIntoView({ behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth', block: 'start' });
        } else {
          window.location.href = FoxTheme.routes.cart_url;
        }
      },
    });
  };

  configureActions();
  document.addEventListener('DOMContentLoaded', configureActions, { once: true });
  window.addEventListener('load', configureActions, { once: true });

  /**
   * A card in a fade slider (Swiper's `fade` effect stacks every slide in place, hidden with opacity) is only seen
   * once its slide is the active one and in view: wait for both, then report once.
   */
  const waitForActiveSlide = (element, slide) => {
    if (element.slideWatcher) return;

    let intersectionObserver = null;
    const watchInView = () => {
      if (intersectionObserver || !slide.classList.contains('swiper-slide-active')) return;
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          intersectionObserver.disconnect();
          intersectionObserver = null;
          if (!slide.classList.contains('swiper-slide-active')) return;
          mutationObserver.disconnect();
          element.slideWatcher = null;
          element.dispatchViewEvent();
        },
        { threshold: 0.5 }
      );
      intersectionObserver.observe(element);
    };
    const mutationObserver = new MutationObserver(watchInView);
    mutationObserver.observe(slide, { attributes: true, attributeFilter: ['class'] });
    element.slideWatcher = {
      disconnect() {
        mutationObserver.disconnect();
        intersectionObserver?.disconnect();
        intersectionObserver = null;
      },
    };
  };

  const defineViewEventElement = (lib) => {
    if (customElements.get('storefront-view-event')) return;

    customElements.define(
      'storefront-view-event',
      class StorefrontViewEvent extends lib.createViewEventElement() {
        /**
         * Runs the trigger again: the library stops watching an `intersect` element after its first view, so an
         * element that must report once per opening (a card in the cart drawer) is watched again.
         */
        rearm() {
          this.slideWatcher?.disconnect();
          this.slideWatcher = null;
          super.disconnectedCallback();
          super.connectedCallback();
        }

        disconnectedCallback() {
          // The observers of a card waiting for its slide would outlive the element (a section reloaded in the editor)
          this.slideWatcher?.disconnect();
          this.slideWatcher = null;
          super.disconnectedCallback?.();
        }

        dispatchViewEvent() {
          // Swiper's loop mode clones slides; the clone of a card is not a second view
          if (this.closest('.swiper-slide-duplicate')) return;

          const fadeSlide = this.closest('.swiper-fade .swiper-slide');
          if (fadeSlide && !fadeSlide.classList.contains('swiper-slide-active')) {
            waitForActiveSlide(this, fadeSlide);
            return;
          }

          // The cart drawer re-renders its body, recommendations included, on every cart change:
          // report each card once per opening (CartDrawer.show() clears the set)
          const cartDrawer = this.getAttribute('view-event-trigger') === 'intersect' && this.closest('cart-drawer');
          if (cartDrawer) {
            cartDrawer.viewedProducts = cartDrawer.viewedProducts || new Set();
            const payload = this.getAttribute('view-event-payload');
            if (cartDrawer.viewedProducts.has(payload)) return;
            cartDrawer.viewedProducts.add(payload);
          }

          super.dispatchViewEvent();
        }
      }
    );
  };

  // Page view first, then the view elements: defining the element reports every `connect` view on the page, so an
  // early define would put them before page:view and before app listeners exist. Both wait for DOMContentLoaded
  // (after every deferred script). `readyState` is already 'interactive' while deferred scripts run, so it can't
  // tell whether DOMContentLoaded happened; the navigation timing can.
  loadLib().then((lib) => {
    if (!lib) return;

    const start = () => {
      document.dispatchEvent(
        new lib.PageViewEvent({
          page: {
            template: FoxTheme.settings.templateName,
            title: document.title,
            url: window.location.href,
          },
        })
      );
      StandardEvents.dispatchSearchRun();
      defineViewEventElement(lib);
    };

    const navigation = performance.getEntriesByType('navigation')[0];
    const domContentLoaded = navigation ? navigation.domContentLoadedEventEnd > 0 : document.readyState === 'complete';
    if (domContentLoaded) start();
    else document.addEventListener('DOMContentLoaded', start, { once: true });
  });
})();
